// ─────────────────────────────────────────────
//  useMovePreceptorOut — the preceptor steps out of the room and the trainee
//  keeps it.
//
//  ⚠️ THIS IS THE OVERRIDE, NOT THE RULE. Owner, 2026-09-07: *"maintain the
//  preceptor in the room dont place in free now… the preceptor only is helping
//  the orientee to manage the room. the supervisor is over both."* An orienting
//  pair therefore leaves the staffing maths exactly as it is and the seat stays
//  the preceptor — right up until a human presses this, which is the moment that
//  stops being true.
//
//  ⚠️ THE PATCH IS RE-DERIVED AT WRITE TIME, FROM THE LIVE ROW. The card hands
//  back a display row built on an earlier render; between that render and the
//  click, an import, another session or the sheet may have changed who is in the
//  room. Looking the assignment up again and asking
//  `promotePreceptorOutPatch` fresh is what stops a stale card writing a person
//  into a seat they left ten minutes ago. It is also the second gate: the
//  domain refuses a pair that is no longer promotable, and the refusal is shown
//  rather than swallowed.
//
//  ⚠️ THE FIELD NAMES ARE NOT SPELLED HERE. `domain/secondProviderWrite` owns
//  them. A second place that knows which fields make up a seat is how a surface
//  starts writing a room nothing else reads.
// ─────────────────────────────────────────────
import { useCallback, useRef } from "react";
import { updateAssignmentRow } from "../../firebase/firestore";
import { promotePreceptorOutPatch } from "../../domain/secondProviderWrite";

export default function useMovePreceptorOut({ assignments, toast, guard }) {
  // Held in refs so `commit` keeps ONE identity across renders. The page passes
  // an inline guard that closes over the live connection band, and listing it
  // as a dependency would mint a new `commit` on every render — which then
  // re-renders every bar on the chart. Same reason as `useSecondDepartCommit`.
  const guardRef = useRef(guard);
  guardRef.current = guard;
  const rowsRef = useRef(assignments);
  rowsRef.current = assignments;

  const commit = useCallback(
    async (cardRow) => {
      const id = cardRow?.assignmentId;
      if (!id) return false;
      if (guardRef.current && !guardRef.current()) return false;

      // ⚠️ EVERY CLAIMING CASE, NOT JUST THE ONE THE CARD NAMED. A room running
      // two cases today yields ONE card row (deduped per person-and-room), and a
      // future case still claims the room. Writing only the first left the rest
      // disagreeing, so the correction silently undid itself the moment the next
      // case started — no error, nothing on screen. The row carries them all.
      const ids = (Array.isArray(cardRow.assignmentIds) && cardRow.assignmentIds.length)
        ? cardRow.assignmentIds
        : [id];
      const targets = ids
        .map((assignmentId) => (rowsRef.current || []).find((a) => a?.id === assignmentId))
        .filter(Boolean);
      if (!targets.length) {
        if (toast) toast.error("That room has changed. Refresh and try again.");
        return false;
      }
      const live = targets[0];

      // ⚠️ THE DOMAIN GATE, ASKED AGAIN. `null` means the row is no longer a
      // promotable orienting pair — the trainee lost their match, the pair was
      // relabelled, or somebody already moved the preceptor. Saying so beats a
      // button that appears to do nothing.
      const patch = promotePreceptorOutPatch(live);
      if (!patch) {
        if (toast) {
          toast.error("That is no longer an orienting pair with a matched trainee.");
        }
        return false;
      }

      const trainee = String(patch.ahpName || "").trim() || "The trainee";
      const preceptor = String(cardRow.preceptorName || "").trim() || "The preceptor";
      const where = cardRow.room || "that room";
      try {
        // Each target re-derives its own patch — see useSwapPair for why one
        // patch must not be reused across two cases in the same room.
        for (const target of targets) {
          const p = target === live ? patch : promotePreceptorOutPatch(target);
          if (p) await updateAssignmentRow(target.id, p);
        }
      } catch (err) {
        console.error("[Timeline] move-preceptor-out write failed:", err);
        if (toast) toast.error("That change could not be saved. Please try again.");
        return false;
      }
      if (toast) {
        toast.success(`${trainee} now holds ${where}. ${preceptor} is free.`);
      }
      return true;
    },
    [toast]
  );

  return { commit };
}
